// 招牌②「簽名入座」（2026-09-25 三站動態設計 Phase B；規格 SSOT＝vault《三站動態設計》§5.1）
//
// 開場標誌（WU 符號原始兩條 path）與描邊動畫住在 index.html（#app 之外、純 CSS），第一次繪製就開始畫——
// 不再「預渲染內容先露出、Vue 掛載後才蓋上黑幕」。html 的內聯黑底也在那裡（修掉首次繪製前的白閃）。
// 這支只負責謝幕：App 掛載、且開場畫完（舊開場描邊＋填色 1.2s；基因開場 1.74s；2023 分鏡 1.7s）後，
// 標誌 FLIP 飛進頁首 logo 的位置（560ms cubic-bezier(.65,0,.35,1)），黑幕同時 400ms 淡出，頁面第一行字上浮 8px、錯開 60ms。
// 一個分頁只跑一次（index.html 的內聯腳本記在 sessionStorage）；2.6s 硬上限（2026-10-04 由 2.2s 放寬，給基因開場；
// 來不及入座就直接淡出；JS 完全沒跑時 CSS 的 intro-deadline 也會在 2.6s 收掉）。任何按鍵或指標按下即略過。
// 減少動態、爬蟲不顯示；預渲染（headless）不碰這段 DOM，靜態 HTML 保留開場標記給真正的訪客。
//
// 2026-10-04 頁首標誌換成內聯 SVG（components/BrandLogo.vue）：入座的目標直接量頁首 WU 符號那條 path（.brand-wu）的
// getBoundingClientRect()，起點量開場 svg 的第一條 path（同一個 WU 本體），兩個「符號本體」對齊——不再靠 PNG 留白量出來的比例框。
export const SEAT = Object.freeze({ minDraw: 1200, geneDraw: 1740, g23Draw: 1700, move: 560, fade: 400, cap: 2600, skip: 200 })
const EASE_SEAT = 'cubic-bezier(.65,0,.35,1)'
const EASE_OUT = 'cubic-bezier(.22,1,.36,1)'
const EASE_IN = 'cubic-bezier(.4,0,1,1)'

// 頁首（桌機組合標誌或手機單符號，看得見的那個）裡 WU 符號本體的位置
export function headerSymbolRect(doc = document) {
  for (const el of doc.querySelectorAll('.site-header .main-logo .brand-wu')) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return { left: r.left, top: r.top, width: r.width, height: r.height }
  }
  return null
}

// 開場要畫多久才入座（看 index.html 決定走哪一種開場）
export const drawTime = root => (root.classList.contains('intro-gene') ? SEAT.geneDraw : root.classList.contains('intro-g23') ? SEAT.g23Draw : SEAT.minDraw)

export function seatIntro({
  doc = document, host = window,
  now = () => host.performance.now(),
  start = host.__introStart,
  target = () => headerSymbolRect(doc),
  lift = () => [...doc.querySelectorAll('#main-content .hero-name > span, #main-content .artist-intro')].slice(0, 3),
  bot = /bot|crawler|spider|headless|prerender/i.test(host.navigator?.userAgent || ''),
} = {}) {
  const intro = doc.getElementById('intro')
  const root = doc.documentElement
  if (!intro || /headless|prerender/i.test(host.navigator?.userAgent || '')) return () => {}
  if (bot || !root.classList.contains('intro-on')) { intro.remove(); root.classList.remove('intro-on'); return () => {} }
  const mark = intro.querySelector('svg')
  const timers = new Set()
  let done = false
  const later = (fn, ms) => { const id = host.setTimeout(() => { timers.delete(id); fn() }, ms); timers.add(id) }
  const finish = () => {
    if (done) return
    done = true
    for (const id of timers) host.clearTimeout(id)
    host.removeEventListener('keydown', skip, true)
    host.removeEventListener('pointerdown', skip, true)
    intro.remove()
    root.classList.remove('intro-on')
  }
  const fadeOut = () => {
    intro.style.animation = 'none'
    intro.style.transition = `opacity ${SEAT.skip}ms ${EASE_IN}`
    intro.style.opacity = '0'
    later(finish, SEAT.skip + 20)
  }
  function skip() { if (!done) { for (const id of timers) host.clearTimeout(id); timers.clear(); fadeOut() } }
  host.addEventListener('keydown', skip, true)
  host.addEventListener('pointerdown', skip, true)

  const elapsed = typeof start === 'number' ? now() - start : drawTime(root)
  if (elapsed > SEAT.cap - SEAT.move - 40) { fadeOut(); return finish }   // 來不及好好入座：直接淡出
  // 起算點：?intro=gene 強制比較時會等基因條圖（intro-wait），圖到了 index.html 會重設 __introStart
  const begin = () => (typeof host.__introStart === 'number' && typeof start === 'number' && host.__introStart > start ? host.__introStart : start)
  // 開場種類可能在掛載之後才定（同上），所以到點時再看一次：還在等圖就再等，換成基因開場就補足它的時間
  const settle = () => {
    if (root.classList.contains('intro-wait')) return later(settle, 50)
    const wait = typeof start === 'number' ? drawTime(root) - (now() - begin()) : 0
    if (wait > 4) return later(settle, wait)
    seat()
  }
  const seat = () => {
    const to = target()
    const box = mark?.getBoundingClientRect()
    const body = mark?.querySelector('path')?.getBoundingClientRect()   // 開場 svg 的第一條 path＝WU 本體
    intro.style.animation = 'none'   // 接手收尾（仍在 2.6s 內結束）
    if (to && box?.width && body?.width) {
      // 以 svg 左上角為原點縮放：本體的左上角要落在頁首符號本體的左上角，寬度對寬度
      const s = to.width / body.width
      const dx = to.left - box.left - s * (body.left - box.left)
      const dy = to.top - box.top - s * (body.top - box.top) + (to.height - body.height * s) / 2
      mark.style.transformOrigin = '0 0'
      mark.style.transition = `transform ${SEAT.move}ms ${EASE_SEAT}`
      mark.getBoundingClientRect()
      mark.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`
    } else if (mark) {
      mark.style.transition = `opacity ${SEAT.fade}ms ${EASE_IN}`
      mark.style.opacity = '0'
    }
    intro.style.transition = `background-color ${SEAT.fade}ms ${EASE_OUT}`
    intro.style.backgroundColor = 'rgba(0,0,0,0)'
    lift().forEach((el, i) => el.animate?.(
      [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
      { duration: 480, delay: 120 + i * 60, easing: EASE_OUT, fill: 'backwards' }))
    later(finish, SEAT.move + 20)
  }
  settle()
  return finish
}
